import React, { useState, useMemo, useEffect } from 'react'
import {
  Search,
  Calendar,
  MapPin,
  Award,
  Quote,
  BookOpen,
  Lightbulb,
  Star,
  ChevronRight,
  ChevronLeft,
  GraduationCap,
  Filter
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'

// Import dữ liệu nhà bác học
import scientistsPart1 from '../../data/scientists/physicsScientists.json'
import scientistsPart2 from '../../data/scientists/physicsScientists_part2.json'
import scientistsPart3 from '../../data/scientists/physicsScientists_part3.json'
import scientistsPart4 from '../../data/scientists/physicsScientists_part4.json'

interface Scientist {
  id: string
  name: string
  nameEn: string
  birth: string
  death?: string
  nationality: string
  field: string[]
  avatar: string
  imageUrl: string
  achievements: string[]
  quote: string
  quoteEn: string
  biography: string
  contributions: string[]
  funFacts: string[]
  relatedTopics: string[]
}

// Helper function để chuyển đổi định dạng emoji cho nhà bác học
const getScientistEmoji = (name: string): string => {
  const emojiMap: Record<string, string> = {
    'Aristotle': '📚', 'Archimedes': '⚗️', 'Ptolemy': '🌍',
    'Ibn al-Haytham': '👁️', 'Nicolaus Copernicus': '☀️', 'Galileo Galilei': '🔭',
    'Johannes Kepler': '🪐', 'René Descartes': '📐', 'Blaise Pascal': '🎲',
    'Robert Hooke': '🔬', 'Christiaan Huygens': '⏱️', 'Isaac Newton': '🍎',
    'Daniel Bernoulli': '💨', 'Benjamin Franklin': '⚡', 'Charles-Augustin de Coulomb': '⚛️',
    'Alessandro Volta': '🔋', 'Thomas Young': '🌈', 'André-Marie Ampère': '🔌',
    'Georg Simon Ohm': '⚡', 'Michael Faraday': '🧲', 'James Clerk Maxwell': '📡',
    'Ludwig Boltzmann': '🌡️', 'Heinrich Hertz': '📻', 'Wilhelm Röntgen': '☢️',
    'Marie Curie': '☢️', 'J.J. Thomson': '⚛️', 'Max Planck': '🔮',
    'Albert Einstein': '🧠', 'Ernest Rutherford': '☢️', 'Niels Bohr': '⚛️',
    'Werner Heisenberg': '🎯', 'Erwin Schrödinger': '🐱', 'Paul Dirac': '✨',
    'Enrico Fermi': '⚛️', 'Richard Feynman': '🎨', 'Murray Gell-Mann': '🔬',
    'Stephen Hawking': '🌌', 'Peter Higgs': '⚛️', 'Abdus Salam': '🔬', 'Vera Rubin': '🌌'
  }
  return emojiMap[name] || '🔬'
}

// Mapping trực tiếp tên file ảnh thực tế trong src/assets/images/scientists/
const getScientistImageUrl = (name: string, index: number): string => {
  const imageFiles = [
    '01_aristotle.jpg',
    '02_archimedes.jpg',
    '03_ptolemy.jpg',
    '04_ibn_al_haytham.jpg',
    '05_nicolaus_copernicus.jpg',
    '06_galileo_galilei.jpg',
    '07_johannes_kepler.jpg',
    '08_rene_descartes.jpg',
    '09_blaise_pascal.jpg',
    '10_robert_hooke.jpg',
    '11_christiaan_huygens.jpg',
    '12_isaac_newton.jpg',
    '13_daniel_bernoulli.jpg',
    '14_benjamin_franklin.jpg',
    '15_charles_augustin_coulomb.jpg',
    '16_alessandro_volta.jpg',
    '17_thomas_young.jpg',
    '18_andre_marie_ampere.jpg',
    '19_georg_simon_ohm.jpg',
    '20_michael_faraday.jpg',
    '21_james_cleark_maxwell.jpg',
    '22_ludwig_boltzmann.jpg',
    '23_heinrich_hertz.jpg',
    '24_wilhelm_rontgen.jpg',
    '25_marie_curie.jpg',
    '26_jj_thomson.jpg',
    '27_max_planck.jpg',
    '28_albert_einstein.jpg',
    '29_ernest_rutherford.jpg',
    '30_niels_bohr.jpg',
    '31_werner_heisenberg.jpg',
    '32_erwin_schrodinger.jpg',
    '33_paul_dirac.jpg',
    '34_enrico_fermi.jpg',
    '35_richard_feynman.jpg',
    '36_murray_gell_mann.jpg',
    '37_stephen_hawking.jpg',
    '38_peter_higgs.jpg',
    '39_abdus_salam.jpg',
    '40_vera_rubin.jpg'
  ]

  const fileName = imageFiles[index] || `${String(index + 1).padStart(2, '0')}_default.jpg`
  const imagePath = `/src/assets/images/scientists/${fileName}`
  return imagePath
}

const NhaBacHoc: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedField, setSelectedField] = useState('all')
  const [selectedScientist, setSelectedScientist] = useState<Scientist | null>(null)
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [touchStart, setTouchStart] = useState<number>(0)
  const [touchEnd, setTouchEnd] = useState<number>(0)

  // Gộp tất cả data từ 4 file JSON và chuyển đổi định dạng
  const scientists: Scientist[] = useMemo(() => {
    const allScientistsData = [
      ...scientistsPart1.scientists,
      ...scientistsPart2.scientists,
      ...scientistsPart3.scientists,
      ...scientistsPart4.scientists
    ]

    return allScientistsData.map((s, index) => {
      // Lấy năm sinh và mất
      const years = s.years.split('-').map(y => y.trim())
      const birthYear = years[0].replace(' TCN', '')
      const deathYear = years[1]?.replace(' TCN', '') || undefined

      // Xác định lĩnh vực
      const fields = ['Vật lí']
      if (s.contributions.some(c => c.title.toLowerCase().includes('toán') || c.title.toLowerCase().includes('phương trình'))) {
        fields.push('Toán học')
      }
      if (s.contributions.some(c => c.title.toLowerCase().includes('thiên văn') || c.title.toLowerCase().includes('vũ trụ'))) {
        fields.push('Thiên văn học')
      }

      return {
        id: s.id,
        name: s.nameVi,
        nameEn: s.name,
        birth: birthYear,
        death: deathYear,
        nationality: s.nationality,
        field: fields,
        avatar: getScientistEmoji(s.name),
        imageUrl: getScientistImageUrl(s.name, index),
        achievements: s.contributions.map(c => c.title).slice(0, 4),
        quote: s.famousQuotes[0] || 'Khám phá chân lý khoa học',
        quoteEn: s.famousQuotes[1] || s.famousQuotes[0] || 'Discover scientific truth',
        biography: Object.values(s.biography.career).join('. '),
        contributions: s.contributions.map(c => `${c.title}: ${c.description}`),
        funFacts: s.biography.keyTraits || [],
        relatedTopics: s.contributions.map(c => c.title).slice(0, 4)
      }
    })
  }, [])

  const filteredScientists = scientists.filter(scientist => {
    const matchesSearch = scientist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scientist.nameEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scientist.field.some(f => f.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesField = selectedField === 'all' || scientist.field.includes(selectedField)

    return matchesSearch && matchesField
  })

  const fields = ['all', ...Array.from(new Set(scientists.flatMap(s => s.field)))]

  // Navigation functions
  const handlePrevious = () => {
    if (currentIndex > 0) {
      const prevScientist = scientists[currentIndex - 1]
      setSelectedScientist(prevScientist)
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (currentIndex < scientists.length - 1) {
      const nextScientist = scientists[currentIndex + 1]
      setSelectedScientist(nextScientist)
      setCurrentIndex(currentIndex + 1)
    }
  }

  // Swipe handling for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // Swipe left - next
      handleNext()
    }

    if (touchStart - touchEnd < -75) {
      // Swipe right - previous
      handlePrevious()
    }
  }

  // Update index when scientist is selected from list
  useEffect(() => {
    if (selectedScientist) {
      const index = scientists.findIndex(s => s.id === selectedScientist.id)
      if (index !== -1) {
        setCurrentIndex(index)
      }
    }
  }, [selectedScientist, scientists])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  }

  if (selectedScientist) {
    return (
      <div className="min-h-screen bg-slate-50/50">
        <Navigation />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        >
          {/* Back Button */}
          <button
            onClick={() => setSelectedScientist(null)}
            className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 mb-6 transition-colors group"
          >
            <div className="p-2 rounded-full bg-white shadow-sm group-hover:shadow-md transition-all">
              <ChevronLeft className="w-5 h-5" />
            </div>
            <span className="font-medium">Quay lại danh sách</span>
          </button>

          {/* Navigation Buttons - Desktop only */}
          <div className="hidden md:flex justify-between items-center mb-6">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 hover:text-primary-600"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Trước</span>
            </button>

            <div className="text-sm font-medium text-gray-500 bg-white px-4 py-1 rounded-full shadow-sm">
              {currentIndex + 1} / {scientists.length}
            </div>

            <button
              onClick={handleNext}
              disabled={currentIndex === scientists.length - 1}
              className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 hover:text-primary-600"
            >
              <span>Tiếp</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Scientist Detail */}
          <motion.div
            className="glass-panel rounded-3xl overflow-hidden shadow-xl"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 relative overflow-hidden">
              <div className="absolute inset-0 bg-black/10 backdrop-blur-[1px]" />
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -z-10 transform translate-x-1/2 -translate-y-1/2" />

              <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 md:gap-12">
                {/* Portrait Image */}
                <div className="flex-shrink-0 relative group md:order-2">
                  <div className="absolute inset-0 bg-white/20 rounded-2xl transform rotate-3 group-hover:rotate-6 transition-transform duration-300"></div>
                  <div className="relative bg-white p-1.5 rounded-2xl shadow-2xl transform group-hover:-translate-y-1 transition-transform duration-300">
                    <div className="w-40 h-52 sm:w-48 sm:h-60 flex items-center justify-center bg-gray-100 rounded-xl overflow-hidden">
                      <img
                        src={selectedScientist.imageUrl}
                        alt={selectedScientist.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const parent = e.currentTarget.parentElement
                          if (parent) {
                            parent.innerHTML = `<div class="text-7xl">${selectedScientist.avatar}</div>`
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 text-center md:text-left text-white">
                  <div className="inline-block px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-sm font-bold mb-4 shadow-sm">
                    {selectedScientist.nationality}
                  </div>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 tracking-tight">{selectedScientist.name}</h1>
                  <p className="text-purple-100 text-lg sm:text-xl mb-6 font-medium">{selectedScientist.nameEn}</p>

                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm">
                    <div className="flex items-center space-x-2 bg-black/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                      <Calendar className="w-4 h-4 text-purple-200" />
                      <span>{selectedScientist.birth} - {selectedScientist.death || 'Hiện tại'}</span>
                    </div>
                    <div className="flex items-center space-x-2 bg-black/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                      <MapPin className="w-4 h-4 text-purple-200" />
                      <span>{selectedScientist.nationality}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Swipe Indicator - Mobile only */}
            <div className="md:hidden p-3 bg-purple-50 border-b border-purple-100 flex items-center justify-center">
              <div className="flex items-center space-x-2 text-xs font-medium text-purple-600 animate-pulse">
                <ChevronLeft className="w-3 h-3" />
                <span>Vuốt để xem tiếp</span>
                <ChevronRight className="w-3 h-3" />
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-10">
              {/* Quote */}
              <div className="relative">
                <div className="absolute -top-4 -left-2 text-6xl text-purple-100 font-serif">"</div>
                <div className="relative z-10 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100 shadow-sm">
                  <blockquote className="text-xl md:text-2xl italic text-gray-800 mb-3 text-center font-serif leading-relaxed">
                    "{selectedScientist.quote}"
                  </blockquote>
                  <p className="text-sm text-gray-500 text-center font-medium uppercase tracking-wide">
                    — {selectedScientist.quoteEn} —
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Biography */}
                  <section>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <div className="p-2 bg-blue-100 rounded-lg mr-3 text-blue-600">
                        <BookOpen className="w-5 h-5" />
                      </div>
                      Tiểu sử
                    </h2>
                    <p className="text-gray-700 leading-relaxed text-lg bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                      {selectedScientist.biography}
                    </p>
                  </section>

                  {/* Contributions */}
                  <section>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                      <div className="p-2 bg-green-100 rounded-lg mr-3 text-green-600">
                        <Lightbulb className="w-5 h-5" />
                      </div>
                      Đóng góp khoa học
                    </h2>
                    <div className="space-y-3">
                      {selectedScientist.contributions.map((contribution, index) => (
                        <div key={index} className="flex items-start space-x-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-green-600 font-bold text-xs">
                            {index + 1}
                          </div>
                          <span className="text-gray-700">{contribution}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                </div>

                {/* Sidebar Content */}
                <div className="space-y-8">
                  {/* Fields */}
                  <section className="bg-gray-50 rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Lĩnh vực</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedScientist.field.map((field, index) => (
                        <span key={index} className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium shadow-sm">
                          {field}
                        </span>
                      ))}
                    </div>
                  </section>

                  {/* Achievements */}
                  <section className="bg-yellow-50/50 rounded-2xl p-6 border border-yellow-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <Award className="w-5 h-5 mr-2 text-yellow-600" />
                      Thành tựu
                    </h3>
                    <ul className="space-y-3">
                      {selectedScientist.achievements.map((achievement, index) => (
                        <li key={index} className="flex items-start space-x-3 text-sm">
                          <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0 fill-current" />
                          <span className="text-gray-700">{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Fun Facts */}
                  <section className="bg-pink-50/50 rounded-2xl p-6 border border-pink-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Có thể bạn chưa biết</h3>
                    <div className="space-y-3">
                      {selectedScientist.funFacts.map((fact, index) => (
                        <div key={index} className="bg-white p-3 rounded-xl text-sm text-gray-600 shadow-sm border border-pink-100">
                          {fact}
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      <Navigation />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="container mx-auto px-4 py-8"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-12 text-center">
          <div className="inline-flex items-center justify-center p-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg mb-6 transform hover:scale-105 transition-transform duration-300">
            <GraduationCap className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            Nhà bác học nổi tiếng
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Khám phá cuộc đời và thành tựu vĩ đại của những người đã thay đổi cách chúng ta nhìn nhận thế giới
          </p>
        </motion.div>

        {/* Search and Filter */}
        <motion.div variants={itemVariants} className="glass-panel rounded-3xl p-8 mb-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-100/50 to-transparent rounded-full blur-3xl -z-10" />

          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-purple-500 transition-colors" />
              <input
                type="text"
                placeholder="Tìm kiếm nhà bác học..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all outline-none"
              />
            </div>

            <div className="w-full md:w-64 relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-600 pointer-events-none">
                <Filter className="w-4 h-4" />
              </div>
              <select
                value={selectedField}
                onChange={(e) => setSelectedField(e.target.value)}
                className="w-full pl-10 pr-8 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none cursor-pointer appearance-none"
              >
                <option value="all">Tất cả lĩnh vực</option>
                {fields.slice(1).map(field => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-400">
                <ChevronRight className="w-4 h-4 rotate-90" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Scientists Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredScientists.map((scientist) => (
            <motion.div
              key={scientist.id}
              variants={itemVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              onClick={() => setSelectedScientist(scientist)}
              className="group glass-panel rounded-2xl overflow-hidden cursor-pointer border border-white/50 hover:border-white/80 transition-all duration-300"
            >
              {/* Card Header */}
              <div className="p-5 bg-gradient-to-r from-blue-500 to-blue-400 relative overflow-hidden min-h-[110px] flex flex-col justify-between">
                <div className="absolute inset-0 bg-black/5 backdrop-blur-[1px]" />
                <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-xl" />

                <div className="relative z-10 flex items-start gap-4">
                  {/* Icon/Avatar Left */}
                  <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center text-3xl shadow-sm shrink-0">
                    {scientist.avatar}
                  </div>

                  {/* Name Right */}
                  <div className="pt-1">
                    <h3 className="text-lg font-bold text-white leading-tight line-clamp-2">
                      {scientist.name}
                    </h3>
                    <p className="text-white/80 text-xs font-medium mt-0.5 line-clamp-1">
                      {scientist.birth} - {scientist.death || '...'}
                    </p>
                  </div>
                </div>

                {/* Nationality Bottom Right */}
                <div className="relative z-10 flex justify-end mt-2">
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-white/20 backdrop-blur-md text-white border border-white/30 shadow-sm">
                    {scientist.nationality}
                  </span>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-5">

                <div className="mb-5">
                  <div className="flex flex-wrap gap-1.5">
                    {scientist.field.slice(0, 2).map((field, index) => (
                      <span key={index} className="px-2.5 py-1 bg-gray-50 text-gray-600 border border-gray-100 rounded-md text-xs font-medium">
                        {field}
                      </span>
                    ))}
                    {scientist.field.length > 2 && (
                      <span className="px-2 py-1 bg-gray-50 text-gray-500 border border-gray-100 rounded-md text-xs font-medium">
                        +{scientist.field.length - 2}
                      </span>
                    )}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 mb-4 border border-blue-100/50 relative">
                  <Quote className="w-4 h-4 text-blue-400 absolute top-3 left-3 opacity-50" />
                  <p className="text-sm italic text-gray-700 line-clamp-3 pl-4">
                    "{scientist.quote}"
                  </p>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-1.5 text-xs text-gray-500">
                    <Award className="w-3.5 h-3.5 text-yellow-500" />
                    <span className="font-medium">{scientist.achievements.length} thành tựu</span>
                  </div>

                  <div className="flex items-center space-x-1 text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    <span>Chi tiết</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredScientists.length === 0 && (
          <motion.div variants={itemVariants} className="text-center py-16 glass-panel rounded-3xl">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Không tìm thấy kết quả</h3>
            <p className="text-gray-500">Thử thay đổi từ khóa tìm kiếm hoặc bộ lọc</p>
          </motion.div>
        )}
      </motion.div>

      <Footer />
    </div>
  )
}

export default NhaBacHoc